import { useEffect, useState } from "react";
import { Download, Smartphone, Share, X } from "lucide-react";

const DISMISS_KEY = "colega_install_dismissed_at";
const DISMISS_DAYS = 14; // hide for 14 days after dismiss

function isIos() {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
         !/safari/i.test(window.navigator.userAgent.toLowerCase()) === false;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches ||
         window.navigator.standalone === true;
}

function dismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / 86400000;
    return ageDays < DISMISS_DAYS;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt — show its own hint after a brief delay
    if (isIos()) {
      const t = setTimeout(() => setShowIos(true), 3500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { console.debug("install prompt choice failed:", e); }
    setDeferred(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { console.debug("localStorage dismiss failed:", e); }
    setDeferred(null);
    setShowIos(false);
  };

  if (installed) return null;
  if (!deferred && !showIos) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] z-[100]"
      data-testid="install-prompt"
    >
      <div className="panel-glow p-5 shadow-2xl border-[hsl(var(--primary))]/40">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Cerrar"
          data-testid="install-prompt-close"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/15 inline-flex items-center justify-center text-[hsl(var(--primary))]">
            <Smartphone size={20} />
          </div>
          <div className="min-w-0 pr-6">
            <div className="serif text-lg leading-snug">Instala Colega Link</div>
            <p className="text-xs text-muted-foreground mt-1">
              {deferred
                ? "Acceso rápido con un toque desde la pantalla de inicio, sin abrir el navegador."
                : "Para instalar en iPhone: toca el ícono \"Compartir\" y luego \"Agregar a Pantalla de inicio\"."}
            </p>
          </div>
        </div>

        {deferred ? (
          <button
            onClick={install}
            className="btn-primary w-full mt-4"
            data-testid="install-prompt-install"
          >
            <Download size={14} className="mr-1.5" />
            Instalar app
          </button>
        ) : (
          <div className="mt-4 text-xs text-muted-foreground panel p-3">
            <div className="flex items-center gap-1.5">
              1. Pulsa <Share size={12} className="inline" /> en Safari
            </div>
            <div className="mt-1">2. Elige &quot;Agregar a Pantalla de inicio&quot;</div>
          </div>
        )}
      </div>
    </div>
  );
}
